/**
 * Shared localStorage helpers for Prometheus engineering projects.
 * Keys match ProjectLifecycleStudio — do not change format.
 */

import type { ProjectWiring } from "@/lib/projectWiringTypes";

const ENGINEERING_KEY = "prometheus_engineering_projects";
const LIFECYCLE_MIRROR_KEY = "prometheus_lifecycle_projects";

/** Studio DEFAULT_PROJECTS id for gabinete PETG + ESP32 pilot. */
export const PILOT_WIRING_PROJECT_ID = "proj-enclosure-ip67-bambu";

export const PILOT_WIRING: ProjectWiring = {
  mcuLabel: "ESP32-S3 DevKit",
  overview:
    "ESP32-S3 montado dentro del gabinete PETG IP67: USB-C por prensaestopas, bus I2C al sensor/OLED y salida digital al relay. Tierra común con el Buck 5V.",
  pins: [
    {
      id: "wp-sda",
      mcu: "ESP32",
      pin: "GPIO 21",
      signal: "SDA",
      to: "Sensor I2C / OLED",
      notes: "Pull-up 4.7kΩ a 3.3V",
    },
    {
      id: "wp-scl",
      mcu: "ESP32",
      pin: "GPIO 22",
      signal: "SCL",
      to: "Sensor I2C / OLED",
    },
    {
      id: "wp-relay",
      mcu: "ESP32",
      pin: "GPIO 5",
      signal: "Digital OUT",
      to: "Relay IN1",
      notes: "Nunca 12V al GPIO",
    },
    {
      id: "wp-gnd",
      mcu: "ESP32",
      pin: "GND",
      signal: "GND",
      to: "GND común Buck + relay",
    },
    {
      id: "wp-vin",
      mcu: "ESP32",
      pin: "5V / VIN",
      signal: "VBUS",
      to: "Salida Buck 5.0V",
    },
  ],
  steps: [
    {
      id: "ws-power",
      title: "Alimentación Buck 5V",
      detail: "Conecta la salida 5V del Buck al VIN del ESP32 y une ambos GND.",
      done: false,
    },
    {
      id: "ws-i2c",
      title: "Bus I2C",
      detail: "Cablea SDA/SCL (GPIO 21/22) al sensor u OLED con pull-ups a 3.3V.",
      done: false,
    },
    {
      id: "ws-relay",
      title: "Salida al relay",
      detail: "GPIO 5 → IN1 del módulo relay; comparte GND con el ESP32.",
      done: false,
    },
    {
      id: "ws-gland",
      title: "Prensaestopas USB",
      detail: "Pasa el cable USB-C por el PG7/PG9 y sella antes de cerrar la tapa PETG.",
      done: false,
    },
  ],
  usbHint: "Web Serial · 115200 baud · Hub USB en /herramientas/mcp",
};

/** Title → wiring seed when id may differ but Studio default title matches. */
export const PILOT_WIRING_BY_TITLE: Record<string, ProjectWiring> = {
  "Gabinetes Herméticos IP67 & Carcasas Paramétricas (Bambu Lab PETG)": PILOT_WIRING,
};

export type ProjectStatus = "in-progress" | "idea-future" | "completed";

export interface PhaseStep {
  id: string;
  title: string;
  description?: string;
  completed?: boolean;
  criticalPoints?: string;
  starterKit?: string;
  actionFirst?: string;
  doneCriteria?: string;
}

export interface ProjectPhase {
  name?: string;
  label?: string;
  steps?: PhaseStep[];
  notes?: string;
}

export interface ProjectLogEntry {
  id: string;
  timestamp: string;
  title: string;
  content: string;
  category?: "progreso" | "problema" | "medicion" | "leccion" | string;
}

/** Minimal UI-facing shape compatible with ProjectLifecycleStudio storage. */
export interface EngineeringProject {
  id: string;
  title: string;
  description?: string;
  status?: ProjectStatus | string;
  discipline?: string;
  priority?: string;
  difficulty?: string;
  ecosystem?: string;
  problemStatement?: string;
  phases?: {
    preparation?: ProjectPhase;
    simulationCad?: ProjectPhase;
    inventoryProcurement?: ProjectPhase;
    construction?: ProjectPhase;
    testingCalibration?: ProjectPhase;
    analysisReview?: ProjectPhase;
  };
  logs?: ProjectLogEntry[];
  keyResults?: string;
  lessonsLearned?: string[];
  /** Per-project Vento-style wiring guide (Task 3). */
  wiring?: ProjectWiring;
  createdAt?: string;
  updatedAt?: string;
}

function resolvePilotWiring(project: EngineeringProject): ProjectWiring | null {
  if (project.id === PILOT_WIRING_PROJECT_ID) return PILOT_WIRING;
  return PILOT_WIRING_BY_TITLE[project.title] ?? null;
}

/**
 * Attach seeded wiring to the gabinete/ESP32 pilot project once if missing.
 * Safe to call on panel mount or before getProject; no-op if already wired.
 */
export function ensurePilotWiringSeed(): void {
  const projects = readProjects();
  if (projects.length === 0) return;
  let changed = false;
  const next = projects.map((p) => {
    if (p.wiring) return p;
    const seed = resolvePilotWiring(p);
    if (!seed) return p;
    changed = true;
    return {
      ...p,
      wiring: structuredClone(seed),
      updatedAt: new Date().toISOString(),
    };
  });
  if (changed) writeProjects(next);
}

function safeParseProjects(raw: string | null): EngineeringProject[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is EngineeringProject =>
        p != null &&
        typeof p === "object" &&
        typeof (p as EngineeringProject).id === "string" &&
        typeof (p as EngineeringProject).title === "string"
    );
  } catch {
    return [];
  }
}

function readProjects(): EngineeringProject[] {
  if (typeof localStorage === "undefined") return [];
  const primary = safeParseProjects(localStorage.getItem(ENGINEERING_KEY));
  if (primary.length > 0) return primary;
  // Fallback if only legacy mirror exists
  return safeParseProjects(localStorage.getItem(LIFECYCLE_MIRROR_KEY));
}

function writeProjects(projects: EngineeringProject[]): void {
  if (typeof localStorage === "undefined") return;
  const json = JSON.stringify(projects);
  localStorage.setItem(ENGINEERING_KEY, json);
  localStorage.setItem(LIFECYCLE_MIRROR_KEY, json);
}

export function listProjects(): EngineeringProject[] {
  return readProjects();
}

export function getProject(id: string): EngineeringProject | null {
  return readProjects().find((p) => p.id === id) ?? null;
}

export function updateProject(
  id: string,
  patch: Partial<EngineeringProject>
): EngineeringProject | null {
  const projects = readProjects();
  const idx = projects.findIndex((p) => p.id === id);
  if (idx < 0) return null;
  const updated: EngineeringProject = {
    ...projects[idx],
    ...patch,
    id: projects[idx].id,
    updatedAt: new Date().toISOString(),
  };
  const next = [...projects];
  next[idx] = updated;
  writeProjects(next);
  return updated;
}

/** Progress % from phase steps (same idea as Studio calculateProjectProgress). */
export function projectProgressPercent(project: EngineeringProject): number {
  const phases = project.phases ? Object.values(project.phases) : [];
  let total = 0;
  let completed = 0;
  for (const phase of phases) {
    if (!phase?.steps) continue;
    for (const step of phase.steps) {
      total++;
      if (step.completed) completed++;
    }
  }
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}
